import {
  clearSupportError,
  rememberSupportError,
  setSupportSession,
  supportContext,
} from "./supportContext";
import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { missingFieldNames, validationFieldLabel } from "./validationFields";

/**
 * The HTTP client.
 *
 * The access token lives in memory only. Putting it in localStorage would make
 * it readable by any injected script; the refresh token is an httpOnly cookie
 * this code cannot see at all, which is the point.
 *
 * `withCredentials` lets the browser send that cookie to /api/auth.
 */
export const api = axios.create({
  baseURL: "/api",
  withCredentials: true,
});

let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
  setSupportSession(token !== null);
  if (token === null) clearSupportError();
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Called when the session cannot be renewed, so the app can show the login screen. */
export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler;
}

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
    const context = supportContext();
    config.headers["X-Support-Screen-Url"] = context.url;
    config.headers["X-Support-Screen-Name"] = encodeURIComponent(
      context.screen_name,
    );
    config.headers["X-Support-User-Agent"] = context.user_agent;
  }
  return config;
});

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

const AUTH_PATHS_WITHOUT_REFRESH = [
  "/auth/refresh",
  "/auth/login",
  "/auth/mfa/verify",
  "/auth/mfa/verify-recovery",
];

// Requests that arrive while a refresh is in flight wait for its outcome
// instead of each firing their own. Without this, a page loading three
// requests at once with an expired token would rotate the refresh token three
// times, and single-use rotation means two of them fail.
let refreshInFlight: Promise<string> | null = null;

async function renewSession(): Promise<string> {
  refreshInFlight ??= api
    .post<{ access_token: string }>("/auth/refresh")
    .then((response) => {
      const token = response.data.access_token;
      setAccessToken(token);
      return token;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    if (
      accessToken &&
      !axios.isCancel(error) &&
      !original?.url?.includes("/auth/") &&
      !original?.url?.includes("/support") &&
      error.response?.status !== 401
    ) {
      // Keep client errors as report context too; SupportEntry only shows network and 5xx failures.
      rememberSupportError({
        message: errorMessage(
          error,
          "Nešto je pošlo krivo. Pokušajte ponovno.",
        ),
        status: error.response?.status ?? null,
        endpoint: (original?.url ?? "").split("?")[0] ?? "",
        request_id:
          typeof error.response?.headers["x-request-id"] === "string"
            ? error.response.headers["x-request-id"]
            : null,
        occurred_at: new Date().toISOString(),
      });
    }

    const isAuthFailure = error.response?.status === 401;
    const isRefreshCall = original?.url?.includes("/auth/refresh");
    const cannotRefresh = AUTH_PATHS_WITHOUT_REFRESH.some((path) =>
      original?.url?.includes(path),
    );
    const canRetry = original && !original._retried && !cannotRefresh;

    if (!isAuthFailure || !canRetry) {
      if (isAuthFailure && isRefreshCall) {
        setAccessToken(null);
        onSessionLost?.();
      }
      return Promise.reject(error);
    }

    original._retried = true;

    try {
      const token = await renewSession();
      original.headers.Authorization = `Bearer ${token}`;
      return await api.request(original);
    } catch (refreshError) {
      setAccessToken(null);
      onSessionLost?.();
      return Promise.reject(refreshError);
    }
  },
);

/**
 * The message to show the user for a failed request.
 *
 * Accept string, structured domain errors and FastAPI validation errors.
 */
export function errorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    if (
      (error.response?.data as { support_reported?: boolean } | undefined)
        ?.support_reported
    )
      return "Nešto je pošlo krivo. Mario je automatski obaviješten.";
    const detail = (error.response?.data as { detail?: unknown } | undefined)
      ?.detail;

    if (typeof detail === "string") {
      return detail;
    }
    if (Array.isArray(detail)) {
      const first: unknown = detail[0];
      if (
        first &&
        typeof first === "object" &&
        "msg" in first &&
        typeof first.msg === "string" &&
        first.msg.trim()
      ) {
        // Framework validation messages are English; show the field in Croatian instead.
        if (
          !/^(Field required|Input should|Value error|String should|List should|Extra inputs|Invalid|Unable to)/.test(
            first.msg,
          )
        )
          return first.msg;
        const loc = "loc" in first && Array.isArray(first.loc) ? first.loc : [];
        const field = loc.length ? String(loc[loc.length - 1]) : "";
        return field
          ? `Provjerite polje: ${validationFieldLabel(field)}.`
          : "Provjerite unesene podatke.";
      }
    } else if (detail && typeof detail === "object") {
      const message =
        "message" in detail && typeof detail.message === "string"
          ? detail.message.trim()
          : "";
      const fields = missingFieldNames(detail).map(validationFieldLabel);
      if (fields.length)
        return `${message ? `${message} ` : ""}Dopunite: ${fields.join(", ")}.`;
      if (message) return message;
    }

    if (!error.response) {
      return "Nema veze s poslužiteljem. Provjerite internetsku vezu.";
    }
  }

  return fallback;
}

export function manualErrorFields(error: unknown): string[] {
  if (!axios.isAxiosError(error)) return [];
  const detail: unknown = (
    error.response?.data as { detail?: unknown } | undefined
  )?.detail;
  return detail &&
    typeof detail === "object" &&
    "code" in detail &&
    detail.code === "needs_manual_fields"
    ? missingFieldNames(detail)
    : [];
}
